/**
 * Comparator Context - Phase 3/3
 * Manages the comparison list with localStorage persistence
 * Enforces same-category comparison rule
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const STORAGE_KEY = 'geots_comparator';
const EXPIRATION_DAYS = 7;

const ComparatorContext = createContext(null);

export function ComparatorProvider({ children }) {
  const [comparatorData, setComparatorData] = useState({
    category_id: null,
    category_name: null,
    category_slug: null,
    products: [],
    expires_at: null
  });
  const [showFloatingBar, setShowFloatingBar] = useState(false);
  const [showCategoryErrorModal, setShowCategoryErrorModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [maxProducts] = useState(3);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        // Check expiration
        if (data.expires_at && new Date(data.expires_at) > new Date()) {
          setComparatorData(data);
          if (data.products.length > 0) {
            setShowFloatingBar(true);
          }
        } else {
          // Expired, clear storage
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error('Error loading comparator data:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Sync across tabs using BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel('geots_comparator_sync');
    
    channel.onmessage = (event) => {
      if (event.data.type === 'update') {
        setComparatorData(event.data.data);
        setShowFloatingBar(event.data.data.products.length > 0);
      }
    };

    return () => channel.close();
  }, []);

  // Save to localStorage whenever data changes
  const saveToStorage = useCallback((data) => {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + EXPIRATION_DAYS);
    
    const dataWithExpiry = {
      ...data,
      expires_at: expiresAt.toISOString()
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataWithExpiry));
    
    // Broadcast to other tabs
    const channel = new BroadcastChannel('geots_comparator_sync');
    channel.postMessage({ type: 'update', data: dataWithExpiry });
    channel.close();
    
    return dataWithExpiry;
  }, []);

  // Check if product is in comparator
  const isInComparator = useCallback((productId) => {
    return comparatorData.products.some(p => p.id === productId);
  }, [comparatorData.products]);

  // Add product to comparator
  const addProduct = useCallback((product, category) => {
    // If comparator is empty, simply add
    if (comparatorData.products.length === 0) {
      const newData = {
        category_id: category.id,
        category_name: category.name_fr || category.name,
        category_slug: category.slug,
        products: [{
          id: product.id,
          name: product.name_fr || product.name,
          image_url: product.images?.[0] || null,
          brand: product.brand,
          model: product.model,
          shop_id: product.shop_id,
          added_at: new Date().toISOString()
        }]
      };
      
      const saved = saveToStorage(newData);
      setComparatorData(saved);
      setShowFloatingBar(true);
      toast.success('Produit ajouté au comparateur');
      return true;
    }

    // Check if same category
    if (comparatorData.category_id !== category.id) {
      // Different category - show error modal
      setPendingProduct({ product, category });
      setShowCategoryErrorModal(true);
      return false;
    }

    // Check if already in comparator
    if (isInComparator(product.id)) {
      toast.info('Ce produit est déjà dans le comparateur');
      return false;
    }

    // Check max products
    if (comparatorData.products.length >= maxProducts) {
      toast.warning(`Maximum ${maxProducts} produits. Retirez-en un d'abord.`);
      return false;
    }

    // Add product
    const newProducts = [...comparatorData.products, {
      id: product.id,
      name: product.name_fr || product.name,
      image_url: product.images?.[0] || null,
      brand: product.brand,
      model: product.model,
      shop_id: product.shop_id,
      added_at: new Date().toISOString()
    }];

    const newData = {
      ...comparatorData,
      products: newProducts
    };

    const saved = saveToStorage(newData);
    setComparatorData(saved);
    toast.success('Produit ajouté au comparateur');
    return true;
  }, [comparatorData, isInComparator, maxProducts, saveToStorage]);

  // Remove product from comparator
  const removeProduct = useCallback((productId) => {
    const newProducts = comparatorData.products.filter(p => p.id !== productId);
    
    if (newProducts.length === 0) {
      // Clear everything
      localStorage.removeItem(STORAGE_KEY);
      setComparatorData({
        category_id: null,
        category_name: null,
        category_slug: null,
        products: [],
        expires_at: null
      });
      setShowFloatingBar(false);
    } else {
      const newData = {
        ...comparatorData,
        products: newProducts
      };
      const saved = saveToStorage(newData);
      setComparatorData(saved);
    }
    
    toast.success('Produit retiré du comparateur');
  }, [comparatorData, saveToStorage]);

  // Clear all products
  const clearComparator = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setComparatorData({
      category_id: null,
      category_name: null,
      category_slug: null,
      products: [],
      expires_at: null
    });
    setShowFloatingBar(false);
    toast.success('Comparateur vidé');
  }, []);

  // Replace comparator with new category (when user confirms)
  const replaceWithNewCategory = useCallback(() => {
    if (!pendingProduct) return;
    
    const { product, category } = pendingProduct;
    
    const newData = {
      category_id: category.id,
      category_name: category.name_fr || category.name,
      category_slug: category.slug,
      products: [{
        id: product.id,
        name: product.name_fr || product.name,
        image_url: product.images?.[0] || null,
        brand: product.brand,
        model: product.model,
        shop_id: product.shop_id,
        added_at: new Date().toISOString()
      }]
    };
    
    const saved = saveToStorage(newData);
    setComparatorData(saved);
    setShowFloatingBar(true);
    setShowCategoryErrorModal(false);
    setPendingProduct(null);
    toast.success('Comparateur remplacé avec le nouveau produit');
  }, [pendingProduct, saveToStorage]);

  // Cancel adding product with different category
  const cancelPendingProduct = useCallback(() => {
    setShowCategoryErrorModal(false);
    setPendingProduct(null);
  }, []);

  // Get share URL
  const getShareUrl = useCallback(() => {
    if (comparatorData.products.length === 0) return null;
    const ids = comparatorData.products.map(p => p.id).join(',');
    return `${window.location.origin}/comparateur?ids=${ids}`;
  }, [comparatorData.products]);

  const value = {
    // State
    comparatorData,
    showFloatingBar,
    showCategoryErrorModal,
    pendingProduct,
    maxProducts,
    
    // Actions
    addProduct,
    removeProduct,
    clearComparator,
    isInComparator,
    replaceWithNewCategory,
    cancelPendingProduct,
    getShareUrl,
    setShowFloatingBar
  };

  return (
    <ComparatorContext.Provider value={value}>
      {children}
    </ComparatorContext.Provider>
  );
}

export function useComparator() {
  const context = useContext(ComparatorContext);
  if (!context) {
    throw new Error('useComparator must be used within a ComparatorProvider');
  }
  return context;
}

export default ComparatorContext;
